/* eslint-disable import/named */
import { Preview } from "@creatomate/preview";
import { CreatomateAnimations, ElementSource, Story, Template } from "chq";
import { getImageFromStory } from "app/utils";
import imagesSizes from "constants/imagesSizes";
import { flattenMultiImageStories } from "components/stories/video-studio/utils";
import { ELEMENT_NAMES, IMAGE_SLIDE_DURATION, IMAGE_TYPE, VIDEO_TYPE } from "components/stories/video-studio/utils/constants";
import getText from "components/editor/getText";
import createElement from "components/stories/video-studio/utils/creatomate/createElement";
import { ImplementorMontageTemplateParams } from "components/stories/video-studio/utils/creatomate/setMontageCustomTemplate";
import findElementByName from "../../utils/findElementByName";

export const createStorySlideStory = async (
  preview: Preview,
  template: Template,
  story: Story,
  storyDuration: number,
  index: number,
  time: number
): Promise<string | undefined> => {
  const { media } = story;
  const { mediaType, url } = media;

  const templateIndex = index % 3;
  const animations = template.animationsPerIndex as unknown as CreatomateAnimations;

  const json = preview.getSource();
  const elements = json?.elements;
  const profilePicElement = findElementByName(elements, ELEMENT_NAMES.PROFILE_PIC1);
  const textElement = findElementByName(elements, ELEMENT_NAMES.CAPTION1);
  const ADJUSTMENT_TIME = profilePicElement?.time - textElement?.time;

  await createElement({
    type: "text",
    text: getText(story.body),
    time: time || ((preview.state?.duration || 1) - 1),
    duration: storyDuration,
    ...template.commonProperties?.text,
    ...template.commonPropertiesPerIndex?.text[templateIndex],
    animations: animations.text[templateIndex]
  }, preview);
  await createElement({
    name: `Profile Pic ${index + 1}`,
    type: "image",
    source: story.creator.avatar.thumbUrl,
    time: time + ADJUSTMENT_TIME,
    duration: storyDuration - ADJUSTMENT_TIME,
    ...template.commonProperties?.profilePic,
    ...template.commonPropertiesPerIndex?.profilePic[templateIndex],
    animations: animations.profilePic[templateIndex]
  }, preview);
  await createElement({
    type: "text",
    text: story.creator.name,
    time: time + ADJUSTMENT_TIME,
    duration: storyDuration - ADJUSTMENT_TIME,
    ...template.commonProperties?.name,
    ...template.commonPropertiesPerIndex?.name[templateIndex],
    animations: animations.name[templateIndex]
  }, preview);

  return createElement({
    name: `${story.creator.name}'s ${mediaType === VIDEO_TYPE ? VIDEO_TYPE : IMAGE_TYPE}`,
    type: mediaType,
    source: mediaType === VIDEO_TYPE ? url : getImageFromStory(imagesSizes.FULL, story),
    track: 1,
    duration: storyDuration,
    time,
    ...template.commonPropertiesPerIndex?.media[templateIndex],
    animations: animations.media[templateIndex]
  }, preview);
};

const setStorySlideshowTemplate = async ({
  template,
  preview,
  selectedStories,
  onStoryUpdate,
  organization,
  prompt
}: ImplementorMontageTemplateParams): Promise<void> => {
  const json = preview.getSource();
  const elements = json?.elements;

  const storyElements = [
    findElementByName(elements, ELEMENT_NAMES.STORY1),
    findElementByName(elements, ELEMENT_NAMES.STORY2),
    findElementByName(elements, ELEMENT_NAMES.STORY3)
  ];
  const textElements = [
    findElementByName(elements, ELEMENT_NAMES.CAPTION1),
    findElementByName(elements, ELEMENT_NAMES.CAPTION2),
    findElementByName(elements, ELEMENT_NAMES.CAPTION3)
  ];
  const profilePicElements = [
    findElementByName(elements, ELEMENT_NAMES.PROFILE_PIC1),
    findElementByName(elements, ELEMENT_NAMES.PROFILE_PIC2),
    findElementByName(elements, ELEMENT_NAMES.PROFILE_PIC3)
  ];
  const profileNameElements = [
    findElementByName(elements, ELEMENT_NAMES.NAME1),
    findElementByName(elements, ELEMENT_NAMES.NAME2),
    findElementByName(elements, ELEMENT_NAMES.NAME3)
  ];

  const logoIntroElement = findElementByName(elements, ELEMENT_NAMES.LOGO_INTRO);
  const logoOutroElement = findElementByName(elements, ELEMENT_NAMES.LOGO_OUTRO);
  const storyTrendElement = findElementByName(elements, ELEMENT_NAMES.STORY_TREND);

  const ADJUSTMENT_TIME = profilePicElements[0]?.time - textElements[0]?.time;

  let modifications = {};
  let nameModifications = {};
  modifications = {
    ...modifications,
    [`${logoIntroElement?.id}`]: organization?.logo?.originalUrl,
    [`${logoOutroElement?.id}`]: organization?.logo?.originalUrl,
    [`${storyTrendElement?.id}.text`]: prompt
  };

  const deleteStoryByIndex = async (index: number): Promise<void> => {
    const json = preview.getSource();
    const { elements } = json;
    let newElements = [...elements];
    newElements = newElements.filter((element: ElementSource) => (
      element.id !== profileNameElements[index]?.id
      && element.id !== profileNameElements[index]?.id
      && element.id !== profilePicElements[index]?.id
      && element.id !== textElements[index]?.id
      && element.id !== storyElements[index]?.id
    ));
    return preview.setSource({ ...json, elements: newElements });
  };

  // Plus intro
  const flattenStories = flattenMultiImageStories(selectedStories);
  let time = logoIntroElement?.duration;

  for (let index = 0; index < flattenStories.length; index += 1) {
    const story = flattenStories[index];
    const { creator, media } = story;
    const { mediaType, url, width = 1, height = 1, duration = 0 } = media;

    const storyDuration = mediaType === IMAGE_TYPE ? IMAGE_SLIDE_DURATION : duration;
    if (index < 3) {
      modifications = {
        ...modifications,
        [`${storyElements[index]?.id}`]: mediaType === VIDEO_TYPE ? url : getImageFromStory(imagesSizes.FULL, story),
        [`${storyElements[index]?.id}.type`]: mediaType,
        [`${storyElements[index]?.id}.time`]: time,
        [`${storyElements[index]?.id}.duration`]: storyDuration,
        [`${textElements[index]?.id}.text`]: getText(story.body),
        [`${textElements[index]?.id}.time`]: time,
        [`${textElements[index]?.id}.duration`]: storyDuration,
        [`${profilePicElements[index]?.id}`]: creator.avatar.thumbUrl,
        [`${profilePicElements[index]?.id}.time`]: time + ADJUSTMENT_TIME,
        [`${profilePicElements[index]?.id}.duration`]: storyDuration - ADJUSTMENT_TIME,
        [`${profileNameElements[index]?.id}.text`]: creator.name,
        [`${profileNameElements[index]?.id}.time`]: time + ADJUSTMENT_TIME,
        [`${profileNameElements[index]?.id}.duration`]: storyDuration - ADJUSTMENT_TIME
      };
      nameModifications = {
        ...nameModifications,
        [`${storyElements[index]?.id}.name`]: mediaType === VIDEO_TYPE ? `${creator.name}'s video` : `${creator.name}'s image`
      };
      onStoryUpdate({ ...story, creatomateId: storyElements[index]?.id });
    } else if (template.animationsPerIndex && !Array.isArray(template.animationsPerIndex)) {
      // eslint-disable-next-line no-await-in-loop
      const elementId = await createStorySlideStory(
        preview, template, story, storyDuration, index, time
      );
      onStoryUpdate({ ...story, creatomateId: elementId });
    }
    // story duration - animation (1 second per transition)
    time += storyDuration;
  }

  // sync outro time
  modifications = {
    ...modifications,
    [`${logoOutroElement?.id}.time`]: time
  };

  for (let index = flattenStories.length; index < 3; index += 1) {
    // eslint-disable-next-line no-await-in-loop
    await deleteStoryByIndex(index);
    // TODO: REview if this sleep is need it
    // this.sleep(1000);
  }

  // Plus outro
  modifications = {
    ...modifications,
    duration: time + logoOutroElement?.duration
  };
  await preview.applyModifications(modifications);
  await preview.applyModifications(nameModifications);
};

export default setStorySlideshowTemplate;
